import { Kb3dObject } from '@kb3d/models/classes/kb3d-object';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { isTrackedClass, ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { ChangeMap } from '../renderer-manager';
import { Processor } from './processor';

export class ReferenceProcessor extends Processor {
    /**
     * referencedNodeId => ( referencedByNodeId => referencedByNode )
     *
     * Any node that is in a nested scene will be keyed by the nested scene ID.
     * If the node is not in a nested scene (or there is no nesting), then the node will be keyed by the bare node id.
     * This applies to the referencedNodeId and the referencedByNodeId.
     * */

    referencesTo = new Map<
        string,
        Map<
            string,
            {
                limitKeys?: Set<string | symbol>;
                ref: Kb3dObject;
                referencePropertyName: string | symbol;
            }
        >
    >();

    orphanedReferences = new Map<
        string,
        {
            referencedBy: Kb3dObject;
            propName: string | symbol;
            referenceId: string;
        }
    >();

    constructor(private viewer: KbViewer) {
        super();
    }

    beforeDeletions(deletions: ChangeMap) {
        const rootScene = this.viewer.sceneNode;
        if (!rootScene || !rootScene._manager) {
            return;
        }
        const rootManager = rootScene._manager;

        deletions.all().forEach(node => {
            if (!node._manager) {
                return;
            }

            let nodeId = node.id;
            if (node._parentScene?.isNested()) {
                nodeId = rootScene.getNestedPathTo(node);
            }

            const referenceTo = this.referencesTo.get(nodeId);
            if (referenceTo && referenceTo.size > 0) {
                referenceTo.forEach((opts, referencedById) => {
                    const referencedBy = rootManager.getById(referencedById);
                    if (referencedBy && (referencedBy as any)[opts.referencePropertyName] === nodeId) {
                        this.orphanedReferences.set(nodeId, {
                            referencedBy,
                            propName: opts.referencePropertyName,
                            referenceId: (referencedBy as any)[opts.referencePropertyName],
                        });
                        (referencedBy as any)[opts.referencePropertyName] = undefined;
                    }
                });
            }
        });
    }

    getReferencesTo(node: Kb3dObject) {
        const rootScene = this.viewer.sceneNode;
        if (!rootScene || !rootScene._manager) {
            return;
        }

        let nodeId = node.id;
        if (node._parentScene?.isNested()) {
            nodeId = rootScene.getNestedPathTo(node);
        }

        return this.referencesTo.get(nodeId);
    }

    beforeUpdates(changes: ChangeMap): void {
        const rootScene = this.viewer.sceneNode;
        if (!rootScene || !rootScene._manager) {
            return;
        }

        changes.all().forEach(node => {
            let nodeId = node.id;
            if (node._parentScene?.isNested()) {
                nodeId = rootScene.getNestedPathTo(node);
            }

            const orphanedReference = this.orphanedReferences.get(nodeId);
            if (orphanedReference) {
                (orphanedReference.referencedBy as any)[orphanedReference.propName] = orphanedReference.referenceId;
            }
        });
    }

    afterUpdates(changes: ChangeMap) {
        const rootScene = this.viewer.sceneNode;
        if (!rootScene || !rootScene._manager) {
            return;
        }

        changes.all().forEach(node => {
            if (!node._manager) {
                return;
            }

            const changedNodeManager = node._manager;
            // This block is for managing/updating the node reference
            // First, check nodes referencing other nodes for updates
            if (node.nodeReferences) {
                const targettingProperties = node.nodeReferences.keys();
                for (const _prop of targettingProperties) {
                    const property = _prop as keyof Kb3dObject;
                    const referenceSettings = node.nodeReferences.get(_prop)!;

                    // If a node's references were changed, update all the references.
                    if (node.changes.has(property)) {
                        if (node[property] && typeof node[property] !== 'string') {
                            console.error(`Non-string id reference value found on ${node.name} property ${property}`);
                        }

                        // First, remove the node from the previous target node's referencing nodes set
                        if (node.originalValues.has(property)) {
                            const originalReferencedNodeId: string = node.originalValues.get(property);
                            if (originalReferencedNodeId) {
                                // Figure out the original target node's key
                                let originalReferencedNodeKey = originalReferencedNodeId;
                                const originalReferencedNode = changedNodeManager.getById(originalReferencedNodeId);
                                if (originalReferencedNode?._parentScene?.isNested()) {
                                    originalReferencedNodeKey = rootScene.getNestedPathTo(originalReferencedNode);
                                }

                                const referencedBy = this.referencesTo.get(originalReferencedNodeKey);
                                if (referencedBy) {
                                    let nestedPathIdSource = node.id;
                                    if (node._parentScene?.isNested()) {
                                        nestedPathIdSource = rootScene.getNestedPathTo(node);
                                    }
                                    referencedBy.delete(nestedPathIdSource);
                                }
                            }
                        }

                        // Set up the new references or delete the reference from entry if the reference was cleared
                        if (node[property]) {
                            const targetNode = node._manager.getById(node[property]);
                            if (targetNode) {
                                if (targetNode instanceof referenceSettings.classRef) {
                                    let nestedPathIdTarget: string = node[property];
                                    let nestedPathIdSource = node.id;
                                    const isNestedSource = node._parentScene?.isNested() || false;

                                    if (nestedPathIdTarget.startsWith('@')) {
                                        const nestedTargetNode = changedNodeManager.getById(nestedPathIdTarget);
                                        if (nestedTargetNode) {
                                            nestedPathIdTarget = rootScene.getNestedPathTo(nestedTargetNode);
                                        }
                                    } else if (isNestedSource) {
                                        // If source node is in a nested scene and the target node id is not a nested path, that means that
                                        // the nested node is targetting a node in it's own scene. But we need to use
                                        // a nested path because there could be multiple instances of the nested node's scene
                                        nestedPathIdTarget = rootScene.getNestedPathTo(targetNode);
                                    }
                                    if (isNestedSource) {
                                        nestedPathIdSource = rootScene.getNestedPathTo(node);
                                    }

                                    let referencedBy = this.referencesTo.get(nestedPathIdTarget);
                                    if (!referencedBy) {
                                        referencedBy = new Map();

                                        this.referencesTo.set(nestedPathIdTarget, referencedBy);
                                    }

                                    referencedBy.set(nestedPathIdSource, {
                                        limitKeys: referenceSettings.limitKeys,
                                        ref: node,
                                        referencePropertyName: property,
                                    });
                                } else {
                                    console.warn(
                                        `Node ${node.name} property ${property} is not referencing a ${referenceSettings.classRef}`
                                    );
                                }
                            }
                        }
                    }
                }
            }
        });

        // Next, check nodes being referenced by other nodes to see if the other nodes will need to be updated
        changes.all().forEach(node => {
            this.triggerChangesOnReferencers(node);
        });
    }

    triggerChangesOnReferencers<T extends Kb3dObject = Kb3dObject>(node: ITrackedClass<T>, changes?: Map<string, any>) {
        const changeMap = changes || new Map(node.changes);

        if (changeMap.size > 0) {
            this.afterCleanup(() => {
                this.triggerChangesOnReferencersSync(node, changeMap);
            });
        }
    }

    private triggerChangesOnReferencersSync<T extends Kb3dObject = Kb3dObject>(
        node: ITrackedClass<T>,
        changes: Map<string, any>,
        transformationPropagation?: boolean
    ) {
        const rootScene = this.viewer.sceneNode;
        if (!rootScene || !rootScene._manager) {
            return;
        }
        const rootManager = rootScene._manager;

        let nodeId = node.id;
        if (node._parentScene?.isNested()) {
            nodeId = rootScene.getNestedPathTo(node);
        }

        const referenceTo = this.referencesTo.get(nodeId);
        if (referenceTo) {
            referenceTo.forEach((referenceSettings, referencedById) => {
                const referencedBy = rootManager.getById(referencedById);
                if (referencedBy && isTrackedClass(referencedBy)) {
                    if (
                        !referenceSettings.limitKeys ||
                        transformationPropagation ||
                        changes.hasAny(...(referenceSettings.limitKeys as Set<keyof Kb3dObject>))
                    ) {
                        referencedBy.setPropertyIsModified(referenceSettings.referencePropertyName, true);
                    }
                }
            });
        }

        // For transformation changes, propagate to the children because the children will also get moved without passing through the change tracker
        // This avoids needing a secondary watcher for node position
        if (
            node instanceof SpaceNode &&
            (changes as Map<keyof SpaceNode, any>).hasAny(
                'position',
                'eulerRotation',
                'rotationAngle',
                'rotationAxis',
                'pivot',
                'scaling'
            )
        ) {
            const children = node.getChildren();
            for (let i = 0; i < children.length; i++) {
                // Always trigger changes on children because they may have different property names
                this.triggerChangesOnReferencersSync(children[i] as ITrackedClass<T>, changes, true);
            }
        }
    }

    dispose() {
        this.referencesTo.clear();
    }
}
